/**
 * RBAC types. Mirrors `apps/authorization` on the backend.
 *
 * Everything here is **presentation input**. The server re-derives authority on
 * every request, so hiding a menu item hides a menu item — it does not close a
 * door. Never use these to decide whether data is safe to show; use them to
 * avoid showing doors that will not open.
 */

/** How far beyond the holder a permission reaches.
 *
 * There is deliberately no `OWN`. Seeing your own records is the baseline —
 * granted by no permission and removable by none — so the *absence* of a
 * permission is own-access. */
export type PermissionScope = "TEAM" | "ALL";

/** A capability code, e.g. `"salary.payslip.view"`. */
export type PermissionCode = string;

export interface Permission {
  id: number;
  code: PermissionCode;
  module: string;
  name: string;
  description: string;
  /** False for resources nobody personally owns — only ALL is offered. */
  supports_scope: boolean;
}

export interface RoleGrant {
  id: number;
  code: PermissionCode;
  module: string;
  name: string;
  scope: PermissionScope;
}

/**
 * A module a role can be locked out of in the employee shell. Keys are the
 * URL app segments the feature gate reads -- the same keys `featureKey` uses
 * in `navData`, so the user shell needs no new lookup.
 */
export type BlockableModule =
  | "attendance"
  | "tasks"
  | "projects"
  | "leave"
  | "salaries"
  | "documents"
  | "inventory"
  | "crm"
  | "expenses";

export interface Role {
  id: number;
  name: string;
  description: string;
  permissions: RoleGrant[];
  /**
   * Modules switched off for this role in the employee shell. A deny-list on
   * top of the baseline, never a grant: an empty list means every module the
   * organization has enabled is reachable.
   */
  blocked_modules: BlockableModule[];
  user_count: number;
  /**
   * Derived, not stored — there is no admin flag. True only when the role
   * holds *every* permission in the catalogue (at ALL scope); holding just
   * `users.role.manage` does not qualify.
   */
  is_admin_role: boolean;
  created_at: string;
  updated_at: string;
}

export interface RoleSummary {
  id: number;
  name: string;
  is_admin_role: boolean;
}

export interface RolePayload {
  name: string;
  description?: string;
  grants?: { code: PermissionCode; scope?: PermissionScope }[];
  /** Whole list, like `grants`; omit to leave the role's blocks untouched. */
  blocked_modules?: BlockableModule[];
}

/** The response of `GET /authorization/me/permissions/`. */
export interface MyPermissions {
  role: { id: number; name: string; is_admin_role: boolean } | null;
  permissions: { code: PermissionCode; scope: PermissionScope }[];
  /**
   * Whether anyone reports to this user. Relational, not a permission —
   * approval chains are a property of the people who report to you, never of
   * your role. Used to decide whether supervisor screens exist at all.
   */
  has_subordinates: boolean;
  /**
   * Whether the admin shell has anything for this user: permissions beyond
   * the employee baseline, decided server-side (T-205). Optional only while
   * an older backend may not send it.
   */
  can_reach_admin_shell?: boolean;
}

/**
 * The permission required to manage roles and permissions. Gates the RBAC
 * screen; it does not by itself make a role read as "Admin" in the UI —
 * that's `is_admin_role`, which requires holding every permission.
 */
export const MANAGE_ROLES: PermissionCode = "users.role.manage";
